import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Tr,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { Hash, Plus } from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  useFoundationV2CuratedRowsForYear,
  useFoundationV2TargetStructure,
  useFoundationV2Years,
} from "../../../../hooks/query/issb";
import { SCOPE_COLORS } from "../../../DataEntryPlanning/components/OrganizationPlanning/components/TaskCard";
import { getFormattedNumValue } from "../../../../utils";
import { buildTargetView } from "../../../../utils/foundationTargetRows";
import { defaultFoundationYear } from "../../../../utils/foundationYearChoice";
import { FOUNDATION_SECTION_ORDER } from "../../FoundationDataV2/foundationTypes";
import { foundationCiteAttrs, foundationCiteText, nextCitationNumber } from "../../../../utils/foundationAnswerLinks";
import { inRoundEntities, roundHasEntities } from "../../../../utils/foundationEntities";

const FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

function sectionRank(group) {
  const i = FOUNDATION_SECTION_ORDER.indexOf(group);
  return i === -1 ? FOUNDATION_SECTION_ORDER.length : i;
}

/* The Foundation Data table's scope chips (FoundationDataTable), matched the same way: by the section name's
   leading "Scope N". Any other section gets the plain uppercase label. */
const scopeChipFor = (category) => {
  const normalised = String(category || "").trim().toLowerCase();
  if (normalised.startsWith("scope 1")) return SCOPE_COLORS.scope1;
  if (normalised.startsWith("scope 2")) return SCOPE_COLORS.scope2;
  if (normalised.startsWith("scope 3")) return SCOPE_COLORS.scope3;
  return null;
};
const categoryTail = (category) =>
  String(category || "")
    .replace(/^\s*scope\s*[123]\s*/i, "")
    .trim();

/** The approved value as the Foundation Data table shows it: formatted when it parses, verbatim otherwise. */
const displayValue = (row) =>
  row.valueNumeric != null ? getFormattedNumValue(row.valueNumeric, 2) : row.dataValue ?? "";

/**
 * Workstream → Core / Custom → data point, for the reporting year this answer's round reports on. Offers
 * APPROVED figures only (`/years/{year}/curated` is the published set), the same ones the AI drafts from:
 * linking an answer to a figure still pending approval would cite a number nobody has signed off.
 *
 * The YEAR is not a choice. It is the round's reporting year (`defaultFoundationYear`: its end year, else
 * the latest that exists — the rule AI Review's Foundation Data card uses), stated in the header. Citing
 * another year's figure into this round's answer is the mistake a picker would make easy.
 *
 * Laid out as the Foundation Data page's table: Core is the workstream's target structure, in its own
 * sections and order (`buildTargetView`), showing only datapoints that hold an approved figure; Custom is
 * every approved figure the structure does not name. Clicking a row selects it; double-click adds.
 */
function FoundationDatapointPickerModal({ isOpen, onClose, onPick }) {
  const round = useSelector((state) => state.issbRound.selectedRound);
  const { data: years, isLoading: isLoadingYears } = useFoundationV2Years({ enabled: isOpen });
  const [group, setGroup] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  /*
    Scoped to the round's entities, exactly as AI Review is (ESG-4019): `/years/{year}/curated` returns EVERY
    entity's figures, so without this a subsidiary's answer was offered — with nothing to tell them apart — the
    other subsidiary's approved "Gross Scope 1" beside its own. Every entity when the round names none.
  */
  const scopedYears = useMemo(
    () => (years ?? []).filter((row) => inRoundEntities(round, row.entityId)),
    [years, round]
  );
  const activeYear = useMemo(() => defaultFoundationYear(years, round), [years, round]);
  // The entities in play, by name — for the header, and to label rows when there is more than one.
  const entityNames = useMemo(() => {
    const names = new Map();
    scopedYears.forEach((row) => {
      if (row.year === activeYear && row.entityId) names.set(row.entityId, row.entityName || "Unnamed entity");
    });
    return names;
  }, [scopedYears, activeYear]);
  const scopedByRound = roundHasEntities(round);

  const groupOptions = useMemo(
    () =>
      [...new Set(scopedYears.filter((row) => row.year === activeYear).map((row) => row.questionGroup))]
        .filter(Boolean)
        .sort((a, b) => sectionRank(a) - sectionRank(b)),
    [scopedYears, activeYear]
  );
  const activeGroup = group && groupOptions.includes(group) ? group : groupOptions[0] ?? null;

  const {
    data: curatedRows,
    isLoading: isLoadingRows,
    isError,
  } = useFoundationV2CuratedRowsForYear(activeYear, { enabled: isOpen && activeYear != null });
  const { data: structure } = useFoundationV2TargetStructure(activeGroup, { enabled: isOpen && !!activeGroup });

  const needle = search.trim().toLowerCase();
  const matchesSearch = useCallback(
    (row) =>
      !needle ||
      (row.dataKey ?? "").toLowerCase().includes(needle) ||
      (row.category ?? "").toLowerCase().includes(needle) ||
      (row.scope ?? "").toLowerCase().includes(needle),
    [needle]
  );

  const groupRows = useMemo(
    () =>
      (curatedRows ?? []).filter(
        (row) => row.questionGroup === activeGroup && inRoundEntities(round, row.entityId)
      ),
    [curatedRows, activeGroup, round]
  );
  const view = useMemo(() => buildTargetView(structure, groupRows), [structure, groupRows]);

  // Core: the structure's sections, each holding only the datapoints with an approved figure.
  const coreSections = useMemo(() => {
    if (!view.hasStructure) return [];
    return view.groups
      .filter((g) => !g.offSchema)
      .map((g) => ({ category: g.category, rows: g.items.flatMap((item) => item.rows).filter(matchesSearch) }))
      .filter((g) => g.rows.length > 0);
  }, [view, matchesSearch]);
  // Custom: what the structure does not name — or, with no structure, every figure, grouped by its category.
  const customRows = useMemo(() => {
    const rows = view.hasStructure
      ? view.groups.filter((g) => g.offSchema).flatMap((g) => g.items.flatMap((item) => item.rows))
      : groupRows;
    return rows.filter(matchesSearch);
  }, [view, groupRows, matchesSearch]);
  const coreCount = coreSections.reduce((sum, g) => sum + g.rows.length, 0);

  const allRows = useMemo(() => [...coreSections.flatMap((g) => g.rows), ...customRows], [coreSections, customRows]);
  const selected = allRows.find((row) => row.id === selectedId) ?? null;

  const close = () => {
    setSelectedId(null);
    setSearch("");
    onClose();
  };
  const pick = (row) => {
    onPick(row, activeYear);
    close();
  };

  // Each row names its entity only when more than one is in play — with one, the header has already said it.
  const rowProps = { selectedId, onSelect: setSelectedId, onPick: pick, showEntity: entityNames.size > 1 };
  const entityLabel = [...entityNames.values()].join(", ");

  return (
    <Modal isOpen={isOpen} onClose={close} size="4xl" scrollBehavior="inside">
      <ModalOverlay bg="rgba(5,31,35,.55)" />
      <ModalContent maxW="880px" borderRadius="16px" h="80vh">
        <ModalHeader pt={6} px={7} pb={4} borderBottom="1px solid" borderColor="gray.200">
          <Text fontSize="12px" fontWeight="700" letterSpacing="0.06em" color="limeShade.600" textTransform="uppercase">
            Add foundation data{activeYear != null ? ` · ${activeYear}` : ""}
            {scopedByRound && entityLabel ? ` · ${entityLabel}` : ""}
          </Text>
          <Text fontSize="heading.l" fontWeight="700" color="darkTeal" mt={1}>
            Insert approved data
          </Text>
          <Text fontSize="13px" color="gray.500" fontWeight="400" mt={1} maxW="680px">
            {activeYear != null
              ? `Approved ${activeYear} data, the year this round reports on. The data is inserted at the cursor and linked to this answer when you save.`
              : "Only approved data is listed."}
          </Text>
        </ModalHeader>
        <ModalCloseButton top={5} right={5} />
        <ModalBody px={7} py={4}>
          {isLoadingYears ? (
            <Spinner size="sm" />
          ) : activeYear == null ? (
            <Text fontSize="body.s" color="gray.500">
              No Foundation Data years yet. Add one on the Foundation Data page and approve some data first.
            </Text>
          ) : groupOptions.length === 0 ? (
            /* The year exists, but not for this round's entity — the figures are filed under another one (on
               staging, the ESG-4016 migration's organisation-named entity). Said, rather than an empty table. */
            <Text fontSize="body.s" color="gray.500">
              {`This round's entity has no ${activeYear} foundation data. If the figures exist, check on the Foundation Data page that they are filed under this round's entity.`}
            </Text>
          ) : (
            <VStack align="stretch" spacing={4}>
              {/* The workstreams, as the Foundation Data list page's sections. Pills rather than a dropdown:
                  there are at most three, and seeing them all says where else a figure might be. */}
              <HStack spacing={2} wrap="wrap">
                {groupOptions.map((g) => {
                  const active = g === activeGroup;
                  return (
                    <Button
                      key={g}
                      size="sm"
                      borderRadius="full"
                      border="1px solid"
                      borderColor={active ? "darkTeal" : "gray.300"}
                      bg={active ? "darkTeal" : "transparent"}
                      color={active ? "white" : "gray.600"}
                      fontWeight={active ? "600" : "normal"}
                      _hover={active ? { bg: "darkTeal" } : { bg: "gray.50", borderColor: "gray.400" }}
                      onClick={() => {
                        setGroup(g);
                        setSelectedId(null);
                      }}
                    >
                      {g}
                    </Button>
                  );
                })}
              </HStack>
              <Input
                size="sm"
                borderRadius="8px"
                placeholder="Search data points"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                {...FOCUS}
              />
              {isLoadingRows ? (
                <Spinner size="sm" />
              ) : isError ? (
                <Text fontSize="body.s" color="gray.500">
                  The {activeYear} foundation data could not be loaded.
                </Text>
              ) : (
                <Tabs variant="primary" isLazy key={activeGroup} defaultIndex={view.hasStructure ? 0 : 1}>
                  <TabList>
                    <Tab isDisabled={!view.hasStructure}>
                      Core
                      <Badge ml={2} variant="subtle" colorScheme="gray">
                        {coreCount}
                      </Badge>
                    </Tab>
                    <Tab>
                      Custom
                      <Badge ml={2} variant="subtle" colorScheme="gray">
                        {customRows.length}
                      </Badge>
                    </Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>
                      {coreSections.length === 0 ? (
                        <EmptyNote search={needle} />
                      ) : (
                        <PickerTable>
                          {coreSections.map((section) => (
                            <SectionRows key={section.category} category={section.category} rows={section.rows} {...rowProps} />
                          ))}
                        </PickerTable>
                      )}
                    </TabPanel>
                    <TabPanel px={0}>
                      {customRows.length === 0 ? (
                        <EmptyNote search={needle} />
                      ) : (
                        <PickerTable>
                          {customRows.map((row) => (
                            <FigureRow key={row.id} row={row} showCategory {...rowProps} />
                          ))}
                        </PickerTable>
                      )}
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter px={7} py={4} borderTop="1px solid" borderColor="gray.200" gap={3}>
          <Text fontSize="13px" color="gray.600" mr="auto" noOfLines={1}>
            {selected ? (
              <>
                <Text as="span" fontWeight="700" color="darkTeal">
                  {selected.dataKey}
                </Text>{" "}
                · {displayValue(selected)} {selected.unit ?? ""}
              </>
            ) : (
              "Select a data point to insert."
            )}
          </Text>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => selected && pick(selected)} isDisabled={!selected}>
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/* The Foundation Data table's frame: bordered, rounded, 14px gray.700 rows. */
function PickerTable({ children }) {
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="12px" overflow="hidden">
      <Table size="sm" sx={{ "& td": { fontSize: "14px", color: "gray.700" } }}>
        <Tbody>{children}</Tbody>
      </Table>
    </Box>
  );
}

/** One structure section: its header row (scope chip, as on the Foundation Data page), then its figures. */
function SectionRows({ category, rows, ...rowProps }) {
  const chip = scopeChipFor(category);
  const tail = chip ? categoryTail(category) : "";
  return (
    <>
      <Tr bg="gray.50">
        <Td colSpan={2} py={3} px={4} borderColor="gray.200">
          <HStack spacing={2}>
            {chip ? (
              <Box
                display="inline-flex"
                alignItems="center"
                gap="4px"
                bg={chip.light}
                color={chip.text}
                fontSize="13px"
                fontWeight="700"
                px="10px"
                h="26px"
                borderRadius="full"
                whiteSpace="nowrap"
              >
                <chip.icon size={14} weight="fill" />
                {chip.label}
              </Box>
            ) : (
              <Text fontSize="13px" fontWeight="700" color="darkTeal" textTransform="uppercase" letterSpacing="0.04em">
                {category}
              </Text>
            )}
            {tail ? (
              <Text fontSize="13px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                {tail}
              </Text>
            ) : null}
            <Badge variant="subtle" colorScheme="gray" fontSize="11px">
              {rows.length}
            </Badge>
          </HStack>
        </Td>
      </Tr>
      {rows.map((row) => (
        <FigureRow key={row.id} row={row} {...rowProps} />
      ))}
    </>
  );
}

const isWholeOrganisation = (scope) =>
  /^\s*(the\s+)?whole\s+organi[sz]ation\s*$/i.test(String(scope ?? ""));

/** One approved figure: name (and what it applies to) left, value and unit right, as the table reads it. */
function FigureRow({ row, showCategory, showEntity, selectedId, onSelect, onPick }) {
  const isSelected = row.id === selectedId;
  // "Whole organisation" is the default reading of a figure, so it is left unsaid; a scope only shows when it
  // narrows the figure (a site, a region), which is what tells two otherwise-identical names apart.
  const scope = isWholeOrganisation(row.scope) ? null : row.scope;
  const detail = [showEntity ? row.entityName : null, showCategory ? row.category : null, scope]
    .filter(Boolean)
    .join(" · ");
  return (
    <Tr
      cursor="pointer"
      bg={isSelected ? "limeShade.50" : "white"}
      boxShadow={isSelected ? "inset 3px 0 0 var(--chakra-colors-lime)" : "none"}
      _hover={{ bg: isSelected ? "limeShade.50" : "gray.50" }}
      onClick={() => onSelect(row.id)}
      onDoubleClick={() => onPick(row)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onPick(row);
        if (e.key === " ") {
          e.preventDefault();
          onSelect(row.id);
        }
      }}
    >
      <Td py={3} px={4} borderColor="gray.100">
        <HStack spacing={2}>
          <Text>{row.dataKey}</Text>
        </HStack>
        {detail ? (
          <Text fontSize="12px" color="gray.500">
            {detail}
          </Text>
        ) : null}
      </Td>
      <Td py={3} px={4} borderColor="gray.100" textAlign="right" whiteSpace="nowrap">
        <Text as="span" fontWeight="600" color="darkTeal" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {displayValue(row)}
        </Text>
        {row.unit ? (
          <Text as="span" ml={2} fontSize="12.5px" color="gray.600">
            {row.unit}
          </Text>
        ) : null}
      </Td>
    </Tr>
  );
}

function EmptyNote({ search }) {
  return (
    <Text fontSize="body.s" color="gray.500" py={2}>
      {search ? "No approved data matches your search." : "No approved data here yet."}
    </Text>
  );
}

/**
 * The answer editor's "Add foundation data" toolbar control: picks an approved figure and cites it at the
 * cursor. The citation is what links the figure to the answer - see utils/foundationAnswerLinks.js.
 *
 * @param {object} props
 * @param {object} props.editor the TipTap editor (RichTextEditor passes it to a function `toolbarRight`)
 */
export default function AddFoundationDataButton({ editor }) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const insert = (row, year) => {
    const number = nextCitationNumber(editor.getHTML(), row.datapointUid);
    editor.chain().focus().insertCite(foundationCiteAttrs(row, year), foundationCiteText(row, number)).run();
  };

  return (
    <>
      {/* A pill in the platform's lime-outline style. The icon is a number with a "+": this inserts a FIGURE
          at the cursor, which the old database icon did not say. */}
      <Button
        size="xs"
        h="28px"
        px={3}
        borderRadius="full"
        bg="white"
        border="1px solid"
        borderColor="lime"
        color="darkTeal"
        fontWeight="600"
        _hover={{ bg: "limeShade.50" }}
        _active={{ bg: "limeShade.100" }}
        leftIcon={
          <Box position="relative" display="inline-flex">
            <Hash size={15} weight="bold" />
            <Box
              position="absolute"
              right="-5px"
              bottom="-4px"
              bg="lime"
              color="darkTeal"
              borderRadius="full"
              display="inline-flex"
              p="1px"
            >
              <Plus size={7} weight="bold" />
            </Box>
          </Box>
        }
        onClick={onOpen}
        title="Insert approved Foundation Data at the cursor"
      >
        Insert data
      </Button>
      {isOpen ? <FoundationDatapointPickerModal isOpen={isOpen} onClose={onClose} onPick={insert} /> : null}
    </>
  );
}
